import { useRef, useState } from 'react';
import SEOHelmet from '@/components/SEOHelmet';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Briefcase, CheckCircle2, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { trackEvent } from '@/utils/analytics';
import { GlowBackground } from '@/components/GlowBackground.tsx';

const CareersPage = () => {
  const { t } = useTranslation('common');
  const pageRef = useRef<HTMLDivElement>(null);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ['start start', 'end end'],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.9]);

  const toggleAccordion = (index: number) => {
    const newValue = activeAccordion === index ? null : index;
    setActiveAccordion(newValue);
    if (newValue !== null && faqItems[index]) {
      trackEvent('FAQ', 'Open', faqItems[index].question);
    }
  };

  interface JobPosition {
    title: string;
    location: string;
    type: string;
    salary?: string;
    description: string;
    requirements: string[];
    slug: string;
  }

  const jobPositions =
    (t('careers.openingsSection.jobPositions', { returnObjects: true }) as JobPosition[]) || [];

  interface FAQ {
    question: string;
    answer: string;
  }

  const faqItems = (t('careers.faqSection.faqs', { returnObjects: true }) as FAQ[]) || [];

  const benefits =
    (t('careers.benefitsSection.benefits', { returnObjects: true }) as string[]) || [];

  return (
    <div ref={pageRef}>
      <SEOHelmet path="/careers" />
      <PageViewTracker path="/careers" title="Careers at Verdea - Join Our Team" />

      <NavBar />
      <GlowBackground colorScheme={'green'} />

      <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
        {/* Background Elements - Green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Dark green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background:
                'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)',
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background:
                'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)',
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          {/* Hero Section */}
          <motion.div
            style={{ opacity: headerOpacity }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {t('careers.heroSection.badge')}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {t('careers.heroSection.heading')}
              <br />
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {t('careers.heroSection.headingHighlight')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('careers.heroSection.subheading')}
            </p>
          </motion.div>

          {/* Why Work at Verdea Section */}
          <div className="relative mb-16 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <Card className="p-8 mb-8 bg-white/80 backdrop-blur-sm dark:bg-gray-800/80">
                <p className="text-xl md:text-2xl font-medium text-[#184c06] dark:text-green-400 mb-6">
                  "{t('careers.whyWorkSection.quote')}"
                </p>
                <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-400">
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('careers.whyWorkSection.paragraph1')}
                  </p>
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('careers.whyWorkSection.paragraph2')}
                  </p>
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('careers.whyWorkSection.paragraph3')}
                  </p>
                </div>
                <div className="flex items-center mt-6">
                  <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-base text-gray-500 dark:text-gray-400">
                    {t('careers.whyWorkSection.location')}
                  </span>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                {t('careers.benefitsSection.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('careers.benefitsSection.subtitle')}
              </p>
            </div>
            <Card className="p-8 bg-white/90 dark:bg-gray-800/90">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-6 text-gray-900 dark:text-gray-100">
                    {t('careers.benefitsSection.perksTitle')}
                  </h3>
                  <ul className="space-y-4">
                    {benefits.slice(0, 5).map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <div className="bg-green-50 rounded-full p-1 mr-3 flex-shrink-0 dark:bg-green-900/20">
                          <CheckCircle2 className="h-4 w-4 text-[#229302] dark:text-green-400" />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="bg-green-50 rounded-xl p-6 mb-6 dark:bg-green-900/20">
                    <h4 className="text-lg font-semibold mb-3 text-[#184c06] dark:text-green-400">
                      {t('careers.benefitsSection.remoteTitle')}
                    </h4>
                    <p className="text-[#229302] dark:text-green-300">
                      {t('careers.benefitsSection.remoteDescription')}
                    </p>
                  </div>
                  <ul className="space-y-4">
                    {benefits.slice(5, 10).map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <div className="bg-green-50 rounded-full p-1 mr-3 flex-shrink-0 dark:bg-green-900/20">
                          <CheckCircle2 className="h-4 w-4 text-[#229302] dark:text-green-400" />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Job Openings Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
            id="openings"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                {t('careers.openingsSection.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('careers.openingsSection.subtitle')}
              </p>
            </div>
            <div className="space-y-6">
              {jobPositions.map((position, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-4 hover:shadow-md transition-shadow duration-300 bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {position.title}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                            {position.location}
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Briefcase className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                            {position.type}
                          </div>
                          {position.salary && (
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              {position.salary}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Link to={`/careers/apply/${position.slug}`}>
                          <Button
                            size="sm"
                            className="bg-[#184c06] hover:bg-[#229302] text-white"
                            onClick={() => trackEvent('Job', 'ApplyClick', position.title)}
                          >
                            {t('careers.openingsSection.applyButton')}
                            <ArrowRight className="ml-2 h-3 w-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                {t('careers.faqSection.title')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('careers.faqSection.subtitle')}
              </p>
            </div>
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="flex items-center justify-between w-full p-5 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    <span className="text-lg font-medium text-gray-900 dark:text-white">
                      {item.question}
                    </span>
                    <span className="ml-6 flex-shrink-0 text-gray-500 dark:text-gray-400">
                      {activeAccordion === index ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </span>
                  </button>
                  {activeAccordion === index && (
                    <div className="p-5 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <p className="text-gray-600 dark:text-gray-400">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto text-center"
          >
            <Card className="p-8 border-green-200 bg-gradient-to-br from-green-50 to-white dark:from-gray-800 dark:to-gray-900 dark:border-green-900/20">
              <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                {t('careers.cta.heading')}
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                {t('careers.cta.subheading')}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/careers/apply/general"
                  className="inline-block"
                  onClick={() => trackEvent('CTA', 'Click', 'General Application')}
                >
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-[#184c06] hover:bg-[#229302] text-white w-auto px-6"
                  >
                    {t('careers.cta.buttonPrimary')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link
                  to="/contact"
                  className="inline-block"
                  onClick={() => trackEvent('CTA', 'Click', 'Careers to Contact')}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-[#184c06] text-[#184c06] hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/20 w-auto px-6"
                  >
                    {t('careers.cta.buttonSecondary')}
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CareersPage;
